/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import ReactDOMServer from 'react-dom/server';
import classnames from 'classnames';
import * as React from 'react';

import { INativeImageProps, INativeImageState, NativeImage } from './native-image';
import msdyn365Commerce from '../../..';

/**
 * Configuration for the main image.
 */
export interface IMainImageProps extends INativeImageProps {}

/**
 * State of the main image.
 */
export interface IMainImageState extends INativeImageState {}

/**
 * React component responsible for rendering main image.
 * It should be used to display the original image.
 */
export class MainImage extends React.PureComponent<IMainImageProps, INativeImageState> {
    /**
     * The default class for main image component.
     */
    private static _mainImageClassName: string = 'msc-main_image';

    /**
     * Initializes instance with the given props.
     * @param {IMainImageProps} props - Configuration for the main placeholder image.
     */
    public constructor(props: IMainImageProps) {
        super(props);
    }

    public renderOptimziedImageWithEvent() {
        if (msdyn365Commerce.isBrowser && msdyn365Commerce.isClientHydrate) {
            return (
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: ''
                    }}
                />
            );
        }
        // client render case after hydrate
        if (msdyn365Commerce.isBrowser) {
            return (
                <span>
                    <NativeImage
                        {...this.props}
                        src={this.props.src}
                        className={classnames(MainImage._mainImageClassName, this.props.className)}
                    />
                </span>
            );
        }
        // server render case
        return this.renderPureHtmlOptimzedImage();
    }

    public renderPureHtmlOptimzedImage() {
        // server render case
        const pictureHtml = ReactDOMServer.renderToString(
            <NativeImage {...this.props} src={this.props.src} className={classnames(MainImage._mainImageClassName, this.props.className)} />
        );
        /* eslint-disable react/no-danger */
        return <span dangerouslySetInnerHTML={{ __html: pictureHtml }}></span>;
    }

    /**
     * Renders main image.
     */
    public render(): React.ReactNode {
        if (this.props.imageFallbackOptimize && this.props.imageSettings?.disableLazyLoad) {
            // To avoid dom-mismatch, flicker issues skip component hydration in the initial page render when image is rendered on server already. Allow renders only after hydrate.
            if (msdyn365Commerce.platformSettings?.enableImageOptimizeEvents) {
                return this.renderOptimziedImageWithEvent();
            }
            return this.renderPureHtmlOptimzedImage();
        } else {
            return (
                <NativeImage
                    {...this.props}
                    src={this.props.src}
                    className={classnames(MainImage._mainImageClassName, this.props.className)}
                />
            );
        }
    }
}
